import { Link as RouterLink } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import Typography from "@mui/material/Typography";
import {
  CUSTOMER_LIST_PATH,
  customerListPathForBranch,
} from "../../../Customer/data";
import { priceBookDetailsPath } from "../../shared/constants";
import { DraftStep, buildDraftSteps, findActiveDraftStep } from "./draftFlow";
import { PricebookDraftDiffState } from "./usePricebookDraftDiff";

const DRAFT_ACCENT_WIDTH = 4;

interface DraftBarProps {
  sourceCode: string;
  /** Null for a reader who may not apply drafts: the bar then only says what the book is. */
  diffState: PricebookDraftDiffState | null;
  /** 00-HO's branch uuid, for the link to its shops; undefined while the branches load. */
  headOfficeBranchUuid: string | undefined;
}

/**
 * Sits on a draft's page: what the draft is, the four steps to its source, and why it cannot be
 * applied when the source moved after the copy.
 */
export default function DraftBar({
  sourceCode,
  diffState,
  headOfficeBranchUuid,
}: DraftBarProps) {
  const diff = diffState?.diff ?? null;
  const steps = buildDraftSteps(sourceCode, diff);
  const activeStep = findActiveDraftStep(steps);
  // Shops are bound to the draft on the customer page, as any other book.
  const testShopsPath = headOfficeBranchUuid
    ? customerListPathForBranch(headOfficeBranchUuid)
    : CUSTOMER_LIST_PATH;

  return (
    <Paper
      sx={{
        borderLeft: (theme) =>
          `${DRAFT_ACCENT_WIDTH}px solid ${theme.palette.warning.main}`,
      }}
    >
      <Stack gap={2}>
        <Typography variant="body2" fontWeight={600}>
          ร่างของ {sourceCode} · ใช้ได้เฉพาะร้านค้าของ 00-HO สาขายังใช้ราคาเดิมของ{" "}
          {sourceCode} จนกว่าจะนำร่างไปใช้
        </Typography>
        {diffState?.errorMessage && (
          <Alert severity="error">{diffState.errorMessage}</Alert>
        )}
        {diff?.source_changed && (
          <Alert
            severity="warning"
            action={
              <Button
                component={RouterLink}
                to={priceBookDetailsPath(diff.source.uuid)}
                color="inherit"
                size="small"
              >
                เปิด {sourceCode}
              </Button>
            }
          >
            สมุดราคาต้นฉบับ {sourceCode} ถูกแก้หลังทำสำเนา นำร่างนี้ไปใช้ไม่ได้
            ต้องทำสำเนาใหม่
          </Alert>
        )}
        {diffState && (
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((step) => (
              <Step key={step.key} completed={step.isCompleted}>
                <StepLabel
                  optional={<StepCaption step={step} testShopsPath={testShopsPath} />}
                >
                  {step.label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        )}
      </Stack>
    </Paper>
  );
}

interface StepCaptionProps {
  step: DraftStep;
  testShopsPath: string;
}

function StepCaption({ step, testShopsPath }: StepCaptionProps) {
  return (
    <Stack alignItems="center">
      <Typography variant="caption" color="text.secondary">
        {step.caption}
      </Typography>
      {step.key === "test" && (
        <Link
          component={RouterLink}
          to={testShopsPath}
          variant="caption"
          underline="hover"
        >
          ผูกร้านที่หน้าลูกค้า (00-HO)
        </Link>
      )}
    </Stack>
  );
}
